import { formatAmount } from 'components/Widget/shared/currency';
import type { CalculatorField, CalculatorFieldValues } from 'components/Widget/shared/types';
import type {
  ApySavingsBreakdownRow,
  ContributionFrequencyKey,
  ContributionFrequencyOption,
  SavingsPeriodKey,
  SavingsPeriodOption,
} from './types';

export const TITLE = 'APY Savings Calculator';

export const DEFAULT_APY = 0.75;

export const DEFAULT_STARTING_BALANCE = 200;

export const DEFAULT_RECURRING_CONTRIBUTION = 100;

export const MAX_FIELD_VALUE = 999999;

/** A recurring contribution of nothing makes the projection a flat interest curve, so the floor is $1. */
export const MIN_RECURRING_CONTRIBUTION = 1;

export const MAX_APY = 15;

export const SAVINGS_PERIOD_OPTIONS: readonly SavingsPeriodOption[] = [
  { key: 'threeMonths', label: '3 Months', bucketMonths: [1, 2, 3] },
  { key: 'sixMonths', label: '6 Months', bucketMonths: [1, 2, 3, 4, 5, 6] },
  { key: 'nineMonths', label: '9 Months', bucketMonths: [1, 3, 6, 9] },
  { key: 'oneYear', label: '1 Year', bucketMonths: [1, 3, 6, 9, 12] },
  { key: 'fiveYears', label: '5 Years', bucketMonths: [12, 24, 36, 48, 60] },
  { key: 'tenYears', label: '10 Years', bucketMonths: [24, 48, 72, 96, 120] },
  { key: 'twentyYears', label: '20 Years', bucketMonths: [12, 60, 120, 180, 240] },
];

export const DEFAULT_SAVINGS_PERIOD: SavingsPeriodOption['key'] = 'fiveYears';

export const CONTRIBUTION_FREQUENCY_OPTIONS: readonly ContributionFrequencyOption[] = [
  { key: 'weekly', label: 'Weekly', periodsPerYear: 52 },
  { key: 'biweekly', label: 'Biweekly', periodsPerYear: 26 },
  { key: 'monthly', label: 'Monthly', periodsPerYear: 12 },
  { key: 'quarterly', label: 'Quarterly', periodsPerYear: 4 },
  { key: 'annually', label: 'Annually', periodsPerYear: 1 },
];

export const DEFAULT_CONTRIBUTION_FREQUENCY: ContributionFrequencyOption['key'] = 'monthly';

// Both dropdowns are closed sets with defaults, so a key that misses is a coding error, not user input.
export const getSavingsPeriod = (key: SavingsPeriodKey): SavingsPeriodOption => {
  const option = SAVINGS_PERIOD_OPTIONS.find(period => period.key === key);

  if (!option) throw new Error(`Unsupported savings period: ${key}`);

  return option;
};

export const getContributionFrequency = (key: ContributionFrequencyKey): ContributionFrequencyOption => {
  const option = CONTRIBUTION_FREQUENCY_OPTIONS.find(frequency => frequency.key === key);

  if (!option) throw new Error(`Unsupported contribution frequency: ${key}`);

  return option;
};

/** Marks every string Legal has yet to supply, so it cannot pass for approved copy in a QA screenshot. */
const PLACEHOLDER = '[PLACEHOLDER COPY - PENDING LEGAL]';

/** Final copy from Legal, received 2026-09-11. */
export const DISCLAIMER =
  'This calculator is for educational purposes only. It makes estimates based on the information you provide, and your actual balance will differ as your deposits, your withdrawals, and the annual percentage yield in effect change over time. It is not an offer of any particular rate, a guarantee of future earnings, or tax, legal, financial, or investment advice.';

export const STARTING_BALANCE_FIELD: CalculatorField = { label: 'Starting balance' };

export const RECURRING_CONTRIBUTION_FIELD: CalculatorField = {
  label: 'Recurring contribution',
  hint: `${PLACEHOLDER} Enter the amount you set aside each time you contribute, not the total for the year.`,
};

export const CONTRIBUTION_FREQUENCY_FIELD: CalculatorField = { label: 'How often do you make a contribution?' };

export const SAVINGS_PERIOD_FIELD: CalculatorField = {
  label: 'Savings time period',
  hint: `${PLACEHOLDER} Choose how long you expect to keep saving before you need the money.`,
};

export const APY_FIELD: CalculatorField = {
  label: 'APY (annual percentage yield)',
  hint: `${PLACEHOLDER} Enter the annual percentage yield your savings account earns, which already accounts for compounding.`,
};

/** One range message per field, covering empty, zero and out-of-range; a red border alone fails WCAG 1.4.1. */
export const STARTING_BALANCE_ERROR = 'Enter a number between 0 and 999,999';

export const RECURRING_CONTRIBUTION_ERROR = 'Enter a number between 1 and 999,999';

export const APY_ERROR = 'Enter an APY between 0% and 15%';

/** Blur normalizes through `formatAmount`, so the defaults start in the shape the fields settle into. */
export const INITIAL_CURRENCY_VALUES: CalculatorFieldValues = {
  [STARTING_BALANCE_FIELD.label]: formatAmount(DEFAULT_STARTING_BALANCE),
  [RECURRING_CONTRIBUTION_FIELD.label]: formatAmount(DEFAULT_RECURRING_CONTRIBUTION),
};

export const INITIAL_APY_VALUES: CalculatorFieldValues = { [APY_FIELD.label]: String(DEFAULT_APY) };

export const CALCULATE_LABEL = 'Calculate';

export const RESET_LABEL = 'Reset';

export const RESULT_COPY = {
  /** The dropdown label already carries the singular/plural, so the eyebrow cannot disagree with it. */
  eyebrow: (periodLabel: string) => `Your total est. savings after ${periodLabel.toLowerCase()}`,
  breakdownHeading: 'Total savings breakdown',
  ctaHeading: 'Fee-free banking with Chime.',
  ctaBody: 'Start banking with Chime today.',
  ctaLabel: 'Get started',
} as const;

/** Mirrors the mock's row order, which is not the bottom-to-top order the chart stacks in. */
export const BREAKDOWN_ROWS: readonly ApySavingsBreakdownRow[] = [
  { key: 'totalContributions', label: 'Total contribution' },
  { key: 'initialDeposit', label: 'Initial deposit' },
  { key: 'totalInterest', label: 'Total interest earned' },
];

/** Self-contained copy for the live-region announcement; the panel itself is too long to hear out. */
export const SUMMARY_COPY = {
  total: (total: string, periodLabel: string) =>
    `Your total estimated savings after ${periodLabel.toLowerCase()} is ${total}.`,
} as const;
